import React, { useState, useEffect } from 'react';
import { Card, StatCard, PageHeader } from '../../components/ui';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import api from '../../services/api';

const COLORS = ['#2ECC71','#3498DB','#9B59B6','#F39C12','#E74C3C'];
const TIP_STYLE = { background:'#111', border:'1px solid rgba(46,204,113,0.2)', borderRadius:8, color:'#fff', fontSize:12 };

export default function Analytics() {
  const [stats, setStats]   = useState({ sent:0, opened:0, clicked:0, replied:0, bounced:0 });
  const [chart, setChart]   = useState([]);
  const [topCamp, setTopCamp] = useState([]);
  const [leadStats, setLeadStats] = useState({});
  const [heatmap, setHeatmap] = useState(null);
  const [days, setDays]     = useState(14);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, [days]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ov, ch, tc, ls] = await Promise.allSettled([
        api.get(`/analytics/overview?days=${days}`),
        api.get(`/analytics/chart?days=${days}`),
        api.get('/analytics/top-campaigns'),
        api.get('/analytics/leads'),
      ]);
      if (ov.status==='fulfilled') setStats(ov.value.data.stats || {});
      if (ch.status==='fulfilled') setChart(ch.value.data.chart || []);
      if (tc.status==='fulfilled') setTopCamp(tc.value.data.data || []);
      if (ls.status==='fulfilled') setLeadStats(ls.value.data.stats || {});
    } catch {} finally { setLoading(false); }
  };

  const rate = (n, d) => d > 0 ? ((n/d)*100).toFixed(1) : '0.0';
  const openRate  = rate(stats.opened,  stats.sent);
  const clickRate = rate(stats.clicked, stats.sent);
  const replyRate = rate(stats.replied, stats.sent);
  const bounceRate= rate(stats.bounced, stats.sent);

  const pieData = [
    { name:'Opened',  value: stats.opened  || 0 },
    { name:'Clicked', value: stats.clicked || 0 },
    { name:'Replied', value: stats.replied || 0 },
    { name:'Bounced', value: stats.bounced || 0 },
    { name:'No Action', value: Math.max(0,(stats.sent||0)-(stats.opened||0)-(stats.bounced||0)) },
  ].filter(d => d.value > 0);

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Real-time email performance data" action={
        <div style={{ display:'flex', gap:6 }}>
          {[7,14,30].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{ padding:'7px 14px', borderRadius:8, border:`1px solid ${days===d?'#2ECC71':'rgba(46,204,113,0.15)'}`, background:days===d?'rgba(46,204,113,0.1)':'transparent', color:days===d?'#2ECC71':'#666', fontSize:12, fontWeight:days===d?700:400, cursor:'pointer' }}>{d}d</button>
          ))}
        </div>
      } />

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:24 }}>
        {[['📤','Sent',stats.sent,'#fff'],['👀','Opened',stats.opened,'#3498DB'],['🖱️','Clicked',stats.clicked,'#9B59B6'],['💬','Replied',stats.replied,'#2ECC71'],['⚠️','Bounced',stats.bounced,'#E74C3C']].map(([icon,label,val,color]) => (
          <StatCard key={label} icon={icon} label={label} value={val?.toLocaleString()} color={color} />
        ))}
      </div>

      {/* Line chart */}
      <Card style={{ marginBottom:20 }}>
        <h3 style={{ fontWeight:700, marginBottom:20 }}>📈 Daily Performance ({days} days)</h3>
        {(chart || []).length === 0 ? (
          <div style={{ textAlign:'center', padding:40, color:'#555' }}>No email activity yet. Start a campaign to see data here.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chart}>
              <XAxis dataKey="date" tick={{ fill:'#555', fontSize:11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill:'#555', fontSize:11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TIP_STYLE} />
              <Legend wrapperStyle={{ fontSize:12, color:'#888' }} />
              <Line type="monotone" dataKey="sent"    stroke="#2ECC71" strokeWidth={2} dot={false} name="Sent" />
              <Line type="monotone" dataKey="opened"  stroke="#3498DB" strokeWidth={2} dot={false} name="Opened" />
              <Line type="monotone" dataKey="clicked" stroke="#9B59B6" strokeWidth={2} dot={false} name="Clicked" />
              <Line type="monotone" dataKey="replied" stroke="#F39C12" strokeWidth={2} dot={false} name="Replied" />
              <Line type="monotone" dataKey="bounced" stroke="#E74C3C" strokeWidth={2} dot={false} name="Bounced" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
        {/* Rates */}
        <Card>
          <h3 style={{ fontWeight:700, marginBottom:20 }}>📊 Rate Overview</h3>
          {[['Open Rate',openRate,'#3498DB'],['Click Rate',clickRate,'#9B59B6'],['Reply Rate',replyRate,'#2ECC71'],['Bounce Rate',bounceRate,'#E74C3C']].map(([label,val,color]) => (
            <div key={label} style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                <span style={{ fontSize:13, color:'#888' }}>{label}</span>
                <span style={{ fontSize:15, fontWeight:800, color }}>{val}%</span>
              </div>
              <div style={{ background:'#1a1a1a', borderRadius:4, height:7 }}>
                <div style={{ width:`${Math.min(parseFloat(val),100)}%`, height:'100%', borderRadius:4, background:color, transition:'width 0.5s' }} />
              </div>
            </div>
          ))}
        </Card>

        {/* Pie chart */}
        <Card>
          <h3 style={{ fontWeight:700, marginBottom:16 }}>🥧 Engagement Breakdown</h3>
          {(pieData || []).length === 0 ? (
            <div style={{ textAlign:'center', padding:40, color:'#555' }}>No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {(pieData || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={TIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
        {/* Top campaigns */}
        <Card>
          <h3 style={{ fontWeight:700, marginBottom:16 }}>🏆 Top Campaigns by Open Rate</h3>
          {(topCamp || []).length === 0 ? (
            <div style={{ textAlign:'center', padding:32, color:'#555', fontSize:13 }}>No campaigns with data yet</div>
          ) : (topCamp || []).map((c, i) => (
            <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:24, height:24, borderRadius:'50%', background:'rgba(46,204,113,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800, color:'#2ECC71' }}>{i+1}</div>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#fff' }}>{c.name}</div>
                  <div style={{ fontSize:11, color:'#555' }}>{c.sent} sent</div>
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:14, fontWeight:800, color:'#3498DB' }}>{c.openRate}%</div>
                <div style={{ fontSize:11, color:'#555' }}>open rate</div>
              </div>
            </div>
          ))}
        </Card>

        {/* Lead stats */}
        <Card>
          <h3 style={{ fontWeight:700, marginBottom:16 }}>👥 Lead Health</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {[['Total Leads', leadStats.total||0, '#fff'],['Active', leadStats.active||0, '#2ECC71'],['Bounced', leadStats.bounced||0, '#E74C3C'],['Unsubscribed', leadStats.unsubscribed||0, '#F39C12'],['High Score', leadStats.highScore||0, '#3498DB']].map(([l,v,c]) => (
              <div key={l} style={{ background:'#1a1a1a', borderRadius:10, padding:'14px 16px', textAlign:'center' }}>
                <div style={{ fontSize:22, fontWeight:800, color:c }}>{v}</div>
                <div style={{ fontSize:11, color:'#555', marginTop:4 }}>{l}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
